import { Children } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { useElementWidth } from '../../hooks/useElementWidth';
import { useAutoColumnsStyles } from './AutoColumns.styles';
import { jsx as _jsx } from "react/jsx-runtime";
const AutoColumns = ({
  minColumnWidth,
  gap,
  divider = false,
  anchorFirst = false,
  children
}) => {
  const {
    ref: gridRef,
    width: gridWidth
  } = useElementWidth();
  const theme = useTheme();
  const childArray = Children.toArray(children);
  const isStacked = gridWidth !== null && gridWidth < minColumnWidth * childArray.length;
  const gridStyle = useAutoColumnsStyles({
    minColumnWidth,
    gap,
    divider,
    isStacked,
    childCount: childArray.length
  });

  // Render {children} directly (not childArray.map) to preserve original React
  // keys — Children.toArray normalises keys and would cause remounts if callers
  // provide explicit keys.
  if (!divider && !anchorFirst) {
    return /*#__PURE__*/_jsx("div", {
      ref: gridRef,
      style: gridStyle,
      children: children
    });
  }
  const anchorWrapperStyle = {
    height: 0,
    minHeight: '100%',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateRows: 'minmax(0, 1fr)'
  };
  const verticalDividerStyle = {
    backgroundColor: theme.colors.borderLight
  };
  const horizontalDividerStyle = {
    height: '1px',
    backgroundColor: theme.colors.borderLight
  };
  const dividerStyle = isStacked ? horizontalDividerStyle : verticalDividerStyle;

  // Non-first children are always wrapped in a div when anchorFirst is true —
  // unconditional wrapping keeps the React key stable across isStacked transitions,
  // preventing unmount/remount when the layout threshold is crossed.
  function columnChild(child, index) {
    if (index === 0 || !anchorFirst) return child;
    const style = isStacked ? {} : anchorWrapperStyle;
    return /*#__PURE__*/_jsx("div", {
      style: style,
      children: child
    }, `col-${index}`);
  }
  if (!divider) {
    return /*#__PURE__*/_jsx("div", {
      ref: gridRef,
      style: gridStyle,
      children: childArray.map((child, i) => columnChild(child, i))
    });
  }
  const items = childArray.flatMap((child, i) => i < childArray.length - 1 ? [columnChild(child, i), /*#__PURE__*/_jsx("div", {
    "aria-hidden": "true",
    style: dividerStyle
  }, `divider-${i}`)] : [columnChild(child, i)]);
  return /*#__PURE__*/_jsx("div", {
    ref: gridRef,
    style: gridStyle,
    children: items
  });
};
export default AutoColumns;